import React, { useMemo, useRef, useContext } from 'react'
import { IColumn } from 'typescript/interfaces/tableUi'
import { IDealsListItem, IsCollectableDealChange } from 'typescript/interfaces/deals'
import { Box, useTheme } from '@material-ui/core'
import SvgIcon from '@material-ui/core/SvgIcon'
import Tooltip from '@material-ui/core/Tooltip/Tooltip'
import Button from 'UI/Button/ButtonWithPreloader'
import { ReactComponent as ArrowUpIcon } from 'assets/svg/ArrowRightTopIcon.svg'
import { ReactComponent as EditIcon } from 'assets/svg/EditIcon.svg'
import { ReactComponent as InfoIcon } from 'assets/svg/InfoIcon.svg'
import { useLastDealsSecondViewStyle } from 'components/Dashboard/style'
import TableComponent, { TableComponentType } from 'UI/Table'
import {
  convertDate,
  convertEmptyField,
  convertToPriceFormat,
  camelCaseWrap,
  convertToPriceFormatWithSign,
  convertToPercentageFormat,
} from 'utils/formatters'
import { DealsStatus, DealsType, SortFieldsDeal } from 'typescript/enums/deals'
import { mathRoundingPercentage } from 'utils/rounding'
import { generatePath, Link, useHistory } from 'react-router-dom'
import { DEAL_INFO_URL, NEW_DEAL_URL } from 'constants/routes'
import { insert } from 'utils/arrayHelpers'
import { ReactComponent as DeleteCircleIcon } from 'assets/svg/DeleteCircleIcon.svg'
import IconButton from '@material-ui/core/IconButton'
import { usePermission } from 'hooks/usePermission'
import { CREATE_CLIENT_DEAL, CREATE_INHOUSE_OUTHOUSE_PERM, DEAL_DELETE_PERM, DEAL_EDIT_PERM } from 'constants/permissions'
import { CompanyType } from 'typescript/interfaces/companies'
import { dealPaidStatusToColor } from 'constants/deals'
import { UserContext } from 'contexts/userContext'
import Typography from '@material-ui/core/Typography'
import { paymentFrequencyTermUnit } from 'typescript/interfaces/paymentFrequency'
import { DealActionButton } from 'components/DealActionButton'
import Checkbox from 'UI/Checkbox'

export const TableLastDealsSecondView = TableComponent as TableComponentType<IDealsListItem>

interface IUseFullDealsProps {
  handleClickStatusButton?: (id: number, status: string) => void
  hideQuesDealAction?: boolean
  isQuesDeals?: boolean
  deleteDeal?: (id: number) => void
  view?: 'funded' | 'submitted' | 'dashboard' | 'my-deals'
  showCollectableColumn?: boolean
  disableCollectableColumn?: boolean
  collectableChanges?: IsCollectableDealChange[]
  handleCollectableChange?: (dealId: number, isCollectable: boolean, originalIsCollectable: boolean) => void
  handleSaveCollectableChanges?: () => void
}

export const useFullDeals = ({
  isQuesDeals,
  hideQuesDealAction,
  handleClickStatusButton,
  deleteDeal,
  view,
  showCollectableColumn,
  disableCollectableColumn = false,
  collectableChanges = [],
  handleCollectableChange,
  handleSaveCollectableChanges,
}: IUseFullDealsProps | undefined = {}) => {
  const {
    state: { roles },
  } = useContext(UserContext)
  const { push } = useHistory()
  const classes = useLastDealsSecondViewStyle()
  const theme = useTheme()
  const handleClickChangeStatus = handleClickStatusButton || (() => {})
  const { hasPermission } = usePermission()

  const collectableHeader = useMemo(
    () =>
      collectableChanges.length > 0 ? (
        <Box display="flex" justifyContent="center">
          <Button 
            variant="outlined" 
            color="secondary" 
            size="small" 
            onClick={handleSaveCollectableChanges}
            disabled={disableCollectableColumn}>
            Save
          </Button>
        </Box>
      ) : undefined,
    [collectableChanges.length, handleSaveCollectableChanges]
  )

  const columns = useMemo<Array<IColumn<IDealsListItem>>>(
    () => [
      ...(showCollectableColumn
        ? [
            {
              label: collectableHeader || '',
              field: 'IsCollectable',
              sorting: false,
              headerCellClassName: classes.headerCell,
              render: (data: IDealsListItem) => {
                const pendingChange = collectableChanges.find(change => change.dealId === data.id)

                return (
                  <Box display="flex" justifyContent="center">
                    <Checkbox
                      checked={pendingChange !== undefined ? pendingChange.isCollectable : data.isCollectable}
                      onChange={event => handleCollectableChange?.(data.id, event.target.checked, data.isCollectable)}
                      disabled={disableCollectableColumn}
                    />
                  </Box>
                )
              },
            },
          ]
        : []),
      {
        label: '#',
        field: 'id',
        sorting: false,
        headerCellClassName: classes.headerCell,
        render: data => <Link to={generatePath(DEAL_INFO_URL, { id: data.id })}>{data.id}</Link>,
      },
      {
        label: 'Date',
        sorting: false,
        field: 'date',
        headerCellClassName: classes.headerCell,
        render: (data: IDealsListItem) => {
          return convertDate(data.status === DealsStatus.approved ? data.approvedDate : data.createdDate)
        },
      },
      ...(view === 'submitted'
        ? [
            {
              label: 'Status',
              field: SortFieldsDeal.status,
              sorting: true,
              headerCellClassName: classes.headerCell,
              render: (data: IDealsListItem) => camelCaseWrap(data.status !== DealsStatus.approved ? data.status : data.paidStatus),
            },
          ]
        : []),
      ...(view === 'funded' || view === 'dashboard' || view === 'my-deals'
        ? [
            {
              label: 'Paid status',
              field: SortFieldsDeal.paidStatus,
              sorting: true,
              headerCellClassName: classes.headerCell,
              render: (data: IDealsListItem) => camelCaseWrap(data.type === DealsType.outhouse ? data.status : data.paidStatus),
            },
          ]
        : []),
      {
        label: 'Business',
        field: SortFieldsDeal.businessName,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => {
          return (
            <Box display="flex" alignItems="center" color="text.secondary" maxWidth={220}>
              {!isQuesDeals && (
                <Tooltip title={data.paidStatus}>
                  <Box
                    mt={-0.5}
                    mr={1}
                    borderRadius="50%"
                    width={6}
                    height={6}
                    style={{
                      backgroundColor: data.status !== 'Approved' ? '#D0DDE8' : dealPaidStatusToColor[data.paidStatus],
                    }}
                  />
                </Tooltip>
              )}
              <Box whiteSpace="nowrap" maxWidth={210} textOverflow="ellipsis" overflow="hidden">
                {convertEmptyField(data.businessName)}
              </Box>
              {data.hasExternalDeals && (
                <Tooltip title="Has external deals">
                  <Box
                    ml={1}
                    px={0.5}
                    borderRadius={4}
                    fontSize={10}
                    fontWeight={600}
                    color="#fff"
                    style={{ backgroundColor: '#FF9800', whiteSpace: 'nowrap' }}
                  >
                    EXT
                  </Box>
                </Tooltip>
              )}
            </Box>
          )
        },
      },
      {
        label: 'Advanced',
        field: SortFieldsDeal.advanced,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => convertToPriceFormat(data.advanceAmount),
      },
      {
        label: 'Payback',
        field: SortFieldsDeal.payback,
        sorting: true,
        headerCellClassName: classes.headerCell,
        bodyCellClassName: classes.accentCell,
        render: data => convertToPriceFormat(data.payback),
      },
      {
        label: 'Frequency',
        field: SortFieldsDeal.paymentFrequency,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => convertEmptyField(data.activeScheduledPaymentInfo.paymentFrequency),
      },
      {
        label: 'Payment Amount',
        tooltip: 'Daily/Weekly/Biweekly Payment Amount',
        field: SortFieldsDeal.paymentAmount,
        sorting: true,
        headerCellClassName: classes.headerCell,
        bodyCellClassName: classes.blueCell,
        render: data => {
          const tooltipTitle = paymentFrequencyToolTipTitle(data)
          const showAmount = data.paidStatus !== 'FullyPaid'

          return (
            <Box fontSize="1rem" display="flex" alignItems="center">
              {convertToPriceFormat(showAmount ? data.activeScheduledPaymentInfo.amount : 0)}
              {tooltipTitle && showAmount && (
                <Box mt={1} ml={1}>
                  <Tooltip placement="top" title={tooltipTitle}>
                    <InfoIcon color={theme.palette.secondary.main} width={16} height={16} />
                  </Tooltip>
                </Box>
              )}
            </Box>
          )
        }
      },
      {
        label: 'Term / F',
        field: SortFieldsDeal.term,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => {
          const active = data.activeScheduledPaymentInfo
          const initial = data.initialScheduledPaymentInfo

          const tooltipTitle = active.term !== initial.term || active.paymentFrequency !== initial.paymentFrequency
            ? `Initial Term: ${mathRoundingPercentage(initial.term)} ${paymentFrequencyTermUnit[initial.paymentFrequency].toLowerCase()}`
            : ''

          return (
            <Box fontSize="1rem" display="flex" alignItems="center">
              {`${mathRoundingPercentage(active.term)}/${mathRoundingPercentage(data.factor)}`}
              {tooltipTitle && (
                <Box mt={1} ml={1}>
                  <Tooltip placement="top" title={tooltipTitle}>
                    <InfoIcon color={theme.palette.secondary.main} width={16} height={16} />
                  </Tooltip>
                </Box>
              )}
            </Box>
          )
        }
      },
      {
        label: 'Net',
        field: SortFieldsDeal.net,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => convertToPriceFormat(data.net),
      },
      {
        label: 'Collected',
        field: SortFieldsDeal.collected,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => {
          return (
            <Box fontSize="1rem" display="flex" alignItems="center">
              <Box mr={1} mb="-3px">
                <SvgIcon fontSize="inherit" viewBox="0 0 16 16" component={ArrowUpIcon} />
              </Box>
              {convertToPriceFormat(data.collected)}
              {data.unallocated > 0 && (
                <Box mt={0.5} ml={1}>
                  <Tooltip placement="top" title={`Unallocated: ${convertToPriceFormat(data.unallocated)}`}>
                    <InfoIcon color="orange" fill="orange" width={16} height={16} />
                  </Tooltip>
                </Box>
              )}
            </Box>
          )
        },
      },
      {
        label: 'Paid Off',
        field: SortFieldsDeal.paidOff,
        headerCellClassName: classes.headerCell,
        render: data => convertToPercentageFormat(data.paidOff),
      },
      {
        label: 'Remaining',
        field: SortFieldsDeal.remaining,
        sorting: true,
        headerCellClassName: classes.headerCell,
        bodyCellClassName: classes.accentCell,
        render: data => convertToPriceFormat(data.remaining > 0 ? data.remaining : 0),
      },
      {
        label: 'Rep',
        field: SortFieldsDeal.rep,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => {
          return data.representatives?.map(rep => (
            <Tooltip title={rep.name || ''}>
              <Box whiteSpace="nowrap" maxWidth={100} textOverflow="ellipsis" overflow="hidden">
                {convertEmptyField(rep.name)}
              </Box>
            </Tooltip>
          ))
        },
      },
      {
        label: 'ISO',
        field: 'iso',
        headerCellClassName: classes.headerCell,
        render: data => {
          return data.isos?.map(iso => (
            <Tooltip title={iso.businessName || ''}>
              <Box whiteSpace="nowrap" maxWidth={100} textOverflow="ellipsis" overflow="hidden">
                {convertEmptyField(iso.businessName)}
              </Box>
            </Tooltip>
          ))
        },
      },
      {
        label: 'Inhouse company',
        field: SortFieldsDeal.inhouseCompany,
        sorting: true,
        headerCellClassName: classes.headerCell,
        render: data => convertEmptyField(data.company?.type === CompanyType.InHouse ? data.company?.name : null),
      },
      {
        label: 'PNL',
        field: SortFieldsDeal.pnl,
        sorting: true,
        headerCellClassName: classes.headerCell,
        bodyCellClassName: classes.accentCell,
        render: data => (
          <Tooltip
            placement="bottom-end"
            title={
              <Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Funding</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(-data.funding)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Commission</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(-data.totalCommission)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Legal Fee</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(-data.legalFee)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>PSF Fee</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(data.psfFee)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Bank Fee</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(data.syndicationFee)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Default fee</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(data.remaining <= 0 ? data.defaultFee : 0)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Wire fee</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(data.remaining <= 0 ? data.wireFee : 0)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Bounce fee</Typography>
                  <Box ml="1rem">{convertToPriceFormatWithSign(data.remaining <= 0 ? data.bounceFee : 0)}</Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Collected</Typography>
                  <Box ml="1rem">
                    {convertToPriceFormatWithSign(
                      data.remaining <= 0 ? data.collected - (data.defaultFee + data.wireFee + data.bounceFee) : data.collected
                    )}
                  </Box>
                </Box>
              </Box>
            }
          >
            <Box>{data.pnl >= 0 ? convertToPriceFormat(data.pnl) : `(${convertToPriceFormat(Math.abs(data.pnl))})`}</Box>
          </Tooltip>
        ),
      }
    ],
    [view, showCollectableColumn, collectableChanges, collectableHeader, handleCollectableChange]
  )

  const columnsForClient = useMemo(() => {
    const dealTypeColumn: IColumn<IDealsListItem> = {
      label: 'Type',
      field: 'type',
      headerCellClassName: classes.headerCell,
      render: data => convertEmptyField(data.type),
    }

    const dealStatusColumn: IColumn<IDealsListItem> = {
      label: 'Status',
      field: 'status',
      headerCellClassName: classes.headerCell,
      render: data => {
        if (data.status !== DealsStatus.approved) return data.status
        return data.paidStatus
      },
    }

    const columnsToRender = insert(columns, 3, dealTypeColumn, dealStatusColumn)

    return columnsToRender
      .map(column => {
        return {
          ...column,
          sorting: false,
        }
      })
      .concat([
        {
          label: 'Edit',
          field: 'edit',
          sorting: false,
          render: data => {
            return (
              <IconButton
                size="small"
                disabled={
                  !hasPermission(DEAL_EDIT_PERM) ||
                  data.status === DealsStatus.approved ||
                  data.status === DealsStatus.declined ||
                  (roles.isClient && data.type === DealsType.outhouse)
                }
                onClick={() => {
                  if (hasPermission(CREATE_INHOUSE_OUTHOUSE_PERM)) {
                    push(
                      generatePath(NEW_DEAL_URL, {
                        id: data.id,
                        view: 'edit',
                        type: data.type === CompanyType.InHouse ? 'inhouse' : 'outhouse',
                        step: 1,
                      })
                    )
                  } else if (hasPermission(CREATE_CLIENT_DEAL)) {
                    push(
                      generatePath(NEW_DEAL_URL, {
                        id: data.id,
                        view: 'edit',
                        type: 'client-deal',
                        step: 1,
                      })
                    )
                  }
                }}
              >
                <EditIcon />
              </IconButton>
            )
          },
        },
        {
          label: 'Delete',
          field: 'delete',
          sorting: false,
          render: data => {
            return (
              <IconButton
                disabled={!hasPermission(DEAL_DELETE_PERM) || (roles.isClient && data.type === DealsType.outhouse)}
                size="small"
                onClick={() => {
                  if (deleteDeal) {
                    deleteDeal(data.id)
                  }
                }}
              >
                <DeleteCircleIcon />
              </IconButton>
            )
          },
        },
      ])
  }, [columns, roles])

  const columnsForDashboard = useMemo(() => {
    return [columns[0] || {}, columns[2] || {}, columns[6] || {}, columns[14] || {}]
  }, [columns])

  const columnsForTopByRemaining = useRef<Array<IColumn<IDealsListItem>>>([
    {
      label: '#',
      field: 'id',
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => <Link to={generatePath(DEAL_INFO_URL, { id: data.id })}>{data.id}</Link>,
    },
    {
      label: 'Business',
      field: SortFieldsDeal.businessName,
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => {
        return (
          <Box display="flex" alignItems="center" color="text.secondary" maxWidth={220}>
            {!isQuesDeals && (
              <Box
                mt={-0.5}
                mr={1}
                borderRadius="50%"
                width={6}
                height={6}
                style={{
                  backgroundColor: data.status !== 'Approved' ? '#D0DDE8' : dealPaidStatusToColor[data.paidStatus],
                }}
              />
            )}
            <Box whiteSpace="nowrap" maxWidth={210} textOverflow="ellipsis" overflow="hidden">
              {convertEmptyField(data.businessName)}
            </Box>
          </Box>
        )
      },
    },
    {
      label: 'Payback',
      field: SortFieldsDeal.payback,
      sorting: false,
      headerCellClassName: classes.headerCell,
      bodyCellClassName: classes.accentCell,
      render: data => convertToPriceFormat(data.payback),
    },
    {
      label: 'Remaining',
      field: SortFieldsDeal.remaining,
      sorting: false,
      headerCellClassName: classes.headerCell,
      bodyCellClassName: classes.accentCell,
      render: data => convertToPriceFormat(data.remaining),
    },
  ])

  const columnsForTopByCollected = useRef<Array<IColumn<IDealsListItem>>>([
    {
      label: '#',
      field: 'id',
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => <Link to={generatePath(DEAL_INFO_URL, { id: data.id })}>{data.id}</Link>,
    },
    {
      label: 'Business',
      field: SortFieldsDeal.businessName,
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => {
        return (
          <Box display="flex" alignItems="center" color="text.secondary" maxWidth={220}>
            {!isQuesDeals && (
              <Box
                mt={-0.5}
                mr={1}
                borderRadius="50%"
                width={6}
                height={6}
                style={{
                  backgroundColor: data.status !== 'Approved' ? '#D0DDE8' : dealPaidStatusToColor[data.paidStatus],
                }}
              />
            )}
            <Box whiteSpace="nowrap" maxWidth={210} textOverflow="ellipsis" overflow="hidden">
              {convertEmptyField(data.businessName)}
            </Box>
          </Box>
        )
      },
    },
    {
      label: 'Payment Amount',
      tooltip: 'Daily/Weekly/Biweekly Payment Amount',
      field: SortFieldsDeal.paymentAmount,
      sorting: false,
      headerCellClassName: classes.headerCell,
      bodyCellClassName: classes.blueCell,
      render: data => convertToPriceFormat(data.activeScheduledPaymentInfo.amount),
    },
    {
      label: 'Collected',
      field: SortFieldsDeal.collected,
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => {
        return (
          <Box fontSize="1rem" display="flex" alignItems="center">
            <Box mr={1} mb="-3px">
              <SvgIcon fontSize="inherit" viewBox="0 0 16 16" component={ArrowUpIcon} />
            </Box>
            {convertToPriceFormat(data.collected)}
            {data.unallocated > 0 && (
              <Box mt={0.5} ml={1}>
                <Tooltip placement="top" title={`Unallocated: ${convertToPriceFormat(data.unallocated)}`}>
                  <InfoIcon color="orange" fill="orange" width={16} height={16} />
                </Tooltip>
              </Box>
            )}
          </Box>
        )
      },
    },
  ])

  const columnsForTopByAdvanced = useRef<Array<IColumn<IDealsListItem>>>([
    {
      label: '#',
      field: 'id',
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => <Link to={generatePath(DEAL_INFO_URL, { id: data.id })}>{data.id}</Link>,
    },
    {
      label: 'Business',
      field: SortFieldsDeal.businessName,
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => {
        return (
          <Box display="flex" alignItems="center" color="text.secondary" maxWidth={220}>
            {!isQuesDeals && (
              <Box
                mt={-0.5}
                mr={1}
                borderRadius="50%"
                width={6}
                height={6}
                style={{
                  backgroundColor: data.status !== 'Approved' ? '#D0DDE8' : dealPaidStatusToColor[data.paidStatus],
                }}
              />
            )}
            <Box whiteSpace="nowrap" maxWidth={210} textOverflow="ellipsis" overflow="hidden">
              {convertEmptyField(data.businessName)}
            </Box>
          </Box>
        )
      },
    },
    {
      label: 'Rep',
      field: SortFieldsDeal.rep,
      sorting: false,
      headerCellClassName: classes.headerCell,
      render: data => {
        return data.representatives?.map(rep => (
          <Tooltip title={rep.name || ''}>
            <Box whiteSpace="nowrap" maxWidth={100} textOverflow="ellipsis" overflow="hidden">
              {convertEmptyField(rep.name)}
            </Box>
          </Tooltip>
        ))
      },
    },
    {
      label: 'Advanced',
      field: SortFieldsDeal.advanced,
      sorting: true,
      headerCellClassName: classes.headerCell,
      render: data => convertToPriceFormat(data.advanceAmount),
    },
  ])

  const columnsForFundingQueDeals = useMemo(() => {
    const filteredColumns = columns.filter(
      c =>
        c.field !== SortFieldsDeal.collected &&
        c.field !== SortFieldsDeal.paidOff &&
        c.field !== SortFieldsDeal.remaining &&
        c.field !== SortFieldsDeal.label &&
        c.field !== SortFieldsDeal.pnl
    )

    return hideQuesDealAction
      ? filteredColumns
      : filteredColumns.concat({
          field: 'actions',
          label: '',
          render: data => <DealActionButton dealId={data.id} onStatusChange={handleClickChangeStatus} />,
        })
  }, [columns, hideQuesDealAction])

  const paymentFrequencyToolTipTitle = (deal: IDealsListItem) => {
    const active = deal.activeScheduledPaymentInfo
    const initial = deal.initialScheduledPaymentInfo
    const isScheduleChanged =
      active?.paymentFrequency !== initial?.paymentFrequency || active?.amount !== initial?.amount

    return isScheduleChanged ? `Initial: ${convertToPriceFormat(initial?.amount)} ${initial?.paymentFrequency}` : ''
  }

  return {
    columns: columns,
    columnsQues: columnsForFundingQueDeals,
    columnsForClient,
    columnsForDashboard,
    columnsForTopByRemaining: columnsForTopByRemaining.current,
    columnsForTopByCollected: columnsForTopByCollected.current,
    columnsForTopByAdvanced: columnsForTopByAdvanced.current,
  }
}
